import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { ContentPasteSearchTwoTone, PriorityHigh } from '@mui/icons-material';
import {
  AcademicAssessmentsContent,
  type WorldUniversityRanking,
  type AcademicAssessment,
} from '.';
import { PAGE_TEXT } from '../../../../lang';
import {
  AssessmentAccordion,
  AssessmentGrid,
  type RowData,
} from '../../../../components';
import {
  useCreateAcademicQualificationAssessment,
  useGetAcademicQualificationData,
} from '../../../../graphql';
import {
  Loading,
  Alert,
  type AlertDetails,
  IconButton,
  DisplayFileData,
} from '../../../../../../components';
import { GPAConversionDialog } from '../../../../../Application/components';
import { PAGE_TEXT as APPLICATION_TEXT } from '../../../../../Application/components/ApplicationForm/lang';

type AcademicAssessmentsBoxsProps = {
  index: number;
  degreeTitle: string;
  academicQualificationId: string;
  initialAssessmentValue: AcademicAssessment;
  allValuesArr: AcademicAssessment[];
  isViewMode: boolean;
  ifRecommendForHKPFS: boolean;
  refetchAssessment?: () => void;
  onJumpToNext?: () => void;
};

const dialogText =
  APPLICATION_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.GPA_DIALOG;
const STEP_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_A;

export const createInfoBox = (
  content: string,
  note?: string,
  exclamationMark?: boolean,
): React.ReactNode => (
  <Box>
    <h2 className="text-[#054783]">{content}</h2>
    {note !== undefined && (
      <span>
        {exclamationMark && <PriorityHigh fontSize="small" color="primary" />}
        <span
          className="italic text-gray-500 text-xs"
          dangerouslySetInnerHTML={{
            __html: note,
          }}
        />
      </span>
    )}
  </Box>
);

export const AcademicAssessmentsBoxs: React.FunctionComponent<
  AcademicAssessmentsBoxsProps
> = props => {
  const { applicationId = '' } = useParams();
  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'info',
  };
  const [openDialogV1, setOpenDialogV1] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);
  const {
    index,
    degreeTitle,
    isViewMode,
    ifRecommendForHKPFS,
    initialAssessmentValue,
    academicQualificationId,
    allValuesArr,
    refetchAssessment = () => {},
    onJumpToNext,
  } = props;
  const defaultssessmentValue: AcademicAssessment = {
    title: degreeTitle,
    convertedGPA: '',
    academicQualificationReferenceId: academicQualificationId,
    selectWorldUniversityRanking: false,
    worldUniversityRanking: {
      score: '',
      universityLevel: '',
      ranking: '',
      range: '',
      rankNumber: null,
      notExactNumber: false,
    },
    selectNationalRanking: false,
    nationalRanking: {
      score: '',
      universityLevel: '',
      ranking: '',
    },
    selectWorldSubjectRanking: false,
    worldSubjectRanking: {
      score: '',
      universityLevel: '',
      ranking: '',
      range: '',
      subjectArea: '',
    },
    selectDisciplineSpecificList: false,
    disciplineSpecificList: {
      score: '',
      universityLevel: '',
      discipline: '',
      ranking: '',
    },
    notApplicable: false,
  };

  const {
    worldUniversityRanking: defWUR,
    nationalRanking: defNR,
    worldSubjectRanking: defWSR,
    disciplineSpecificList: defDSL,
  } = defaultssessmentValue;
  const {
    selectDisciplineSpecificList = false,
    selectNationalRanking = false,
    selectWorldSubjectRanking = false,
    selectWorldUniversityRanking = false,
  } = initialAssessmentValue;

  const initialValue = {
    ...initialAssessmentValue,
    ...(selectWorldUniversityRanking ? {} : { worldUniversityRanking: defWUR }),
    ...(selectNationalRanking ? {} : { nationalRanking: defNR }),
    ...(selectWorldSubjectRanking ? {} : { worldSubjectRanking: defWSR }),
    ...(selectDisciplineSpecificList ? {} : { disciplineSpecificList: defDSL }),
  };

  const {
    academicQualificationData,
    loading,
    error: academicQualificationDataError,
  } = useGetAcademicQualificationData(academicQualificationId);

  const {
    degreeLevel,
    institutionName,
    countryRegion,
    officialMark,
    officialClassRanking,
    gpaIndicator,
    rankIndicator,
    outOfGpa,
    outOfRank,
    awardClassification,
    marks, // optional field
    convertGPARuleId,
    convertedMark,
    certificates,
    transcripts,
    markExplanations,
    otherDocuments,
    classRankProofs,
    digitToLetterRule,
  } = academicQualificationData;

  const hasMarks = Boolean(marks.length); // false means no need to show the grading detail

  const InfoDetailsRowData: RowData[] = [
    {
      label: createInfoBox(STEP_TEXT.DEGREE_LEVEL),
      value: degreeLevel,
    },
    {
      label: createInfoBox(STEP_TEXT.INSTITUTION_NAME),
      value: institutionName,
    },
    {
      label: createInfoBox(STEP_TEXT.INSTITUTION_COUNTRY),
      value: countryRegion,
    },
    {
      label: createInfoBox(STEP_TEXT.OFFICIAL_AWARD_GPA),
      value: convertedMark,
    },
    {
      label: createInfoBox(
        STEP_TEXT.OFFICIAL_GPA.CONTENT,
        hasMarks
          ? STEP_TEXT.OFFICIAL_GPA.NOTE_2
          : STEP_TEXT.OFFICIAL_GPA.NOTE_1,
        hasMarks,
      ),
      value: gpaIndicator ? (
        'N/A'
      ) : (
        <div className="flex items-center">
          <h2>
            {officialMark} <small>{STEP_TEXT.OUT_OF}</small>
            {outOfGpa}
          </h2>
          {hasMarks && (
            <IconButton
              title="Go Grading Detail"
              className="!ml-4 !p-1"
              size="small"
              onClick={() => {
                setOpenDialogV1(true);
              }}
              icon={<ContentPasteSearchTwoTone color="primary" />}
            />
          )}
        </div>
      ),
    },
    {
      label: createInfoBox(
        STEP_TEXT.CLASS_RANKING.CONTENT,
        STEP_TEXT.CLASS_RANKING.NOTE,
      ),
      value: rankIndicator ? (
        'N/A'
      ) : (
        <>
          {officialClassRanking} <small>{STEP_TEXT.OUT_OF} </small>
          {outOfRank}
        </>
      ),
    },
    {
      label: createInfoBox(
        STEP_TEXT.AWARD_CLASSIFICATION.CONTENT,
        STEP_TEXT.AWARD_CLASSIFICATION.NOTE,
      ),
      value: awardClassification,
    },

    {
      label: createInfoBox(STEP_TEXT.TRANSCRIPT),
      value: <DisplayFileData fileData={transcripts} />,
    },
    {
      label: createInfoBox(STEP_TEXT.CERTIFICATE),
      value: <DisplayFileData fileData={certificates} />,
    },
    {
      label: createInfoBox(STEP_TEXT.RANKING_PROOF),
      value: <DisplayFileData fileData={classRankProofs} />,
    },
    {
      label: createInfoBox(STEP_TEXT.SYSTEM_EXPLANATION),
      value: <DisplayFileData fileData={markExplanations} />,
    },
    {
      label: createInfoBox(STEP_TEXT.OTHER_DOCUMENTS),
      value: <DisplayFileData fileData={otherDocuments} />,
    },
  ];

  const createAcademicQualificationAssessment =
    useCreateAcademicQualificationAssessment(refetchAssessment);
  const handleSubmit = async (value: AcademicAssessment): Promise<void> => {
    const {
      convertedGPA,
      selectWorldUniversityRanking,
      worldUniversityRanking,
      selectNationalRanking,
      nationalRanking,
      selectWorldSubjectRanking,
      worldSubjectRanking,
      selectDisciplineSpecificList,
      disciplineSpecificList,
      ...others
    } = value;

    const processedWorldUniversityRanking: WorldUniversityRanking = {
      score: worldUniversityRanking?.score ?? '',
      universityLevel: worldUniversityRanking?.universityLevel ?? '',
      ranking: worldUniversityRanking?.ranking ?? '',
      range: worldUniversityRanking?.range ?? '',
      rankNumber: worldUniversityRanking?.rankNumber ?? -1,
      notExactNumber: worldUniversityRanking?.notExactNumber ?? false,
    };
    if (ifRecommendForHKPFS) {
      if (!selectNationalRanking && !selectWorldUniversityRanking) {
        setAlertDetails({
          alertContent:
            'For HKPFS, either World University Ranking or National Ranking must be completed',
          alertType: 'warning',
        });
        setOpenAlert(true);
        return;
      }
    }

    const filteredAQAssessmentInputs: AcademicAssessment = {
      convertedGPA: Number(convertedGPA),
      selectWorldUniversityRanking,
      selectNationalRanking,
      selectWorldSubjectRanking,
      selectDisciplineSpecificList,
      ...(selectWorldUniversityRanking
        ? { worldUniversityRanking: processedWorldUniversityRanking }
        : null),
      ...(selectNationalRanking ? { nationalRanking } : null),
      ...(selectWorldSubjectRanking ? { worldSubjectRanking } : null),
      ...(selectDisciplineSpecificList ? { disciplineSpecificList } : null),
      ...others,
    };

    const targetDegree = value.title;
    const indexInhistory = allValuesArr.findIndex(
      item => item.title === targetDegree,
    );
    //   go update the item whose index == indexInhistory
    const newAllValuesArr = [
      ...allValuesArr.slice(0, indexInhistory),
      filteredAQAssessmentInputs,
      ...allValuesArr.slice(indexInhistory + 1),
    ] as AcademicAssessment[];
    try {
      const variables = {
        input: {
          applicationId,
          academicQualificationAssessmentInputs: newAllValuesArr,
        },
      };
      await createAcademicQualificationAssessment({
        variables,
      });
      alertDetailDef = {
        alertContent: `Saved Academic Qualification - ${
          index
        } Assessment Successfully.`,
        alertType: 'success',
      };
      refetchAssessment();
    } catch (error: any) {
      alertDetailDef = {
        alertContent:
          `Saved Academic Qualification - ${index} Assessment Failed.` +
          (error as Error).message,
        alertType: 'error',
      };
    }
    setAlertDetails(alertDetailDef);
    setOpenAlert(true);
  };

  if (academicQualificationDataError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            'Error Occurs.' +
            (academicQualificationDataError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleDialogV1Close = (): void => {
    setOpenDialogV1(false);
  };

  const GPAConvertDisplay = {
    marks,
    convertGPARuleId,
    institutionName,
    countryRegion,
    gpaIndicator,
  };

  const dialogField = {
    officialMark,
    outOfGpa,
    convertedMark,
  };
  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      {openDialogV1 && (
        <GPAConversionDialog
          viewMode={true}
          dialogTitle={dialogText.DIALOG_V1_TITLE}
          openDialog={openDialogV1}
          onDialogClose={handleDialogV1Close}
          dialogContent={{
            courses: marks,
            officialMark,
            digitToLetterRule,
          }}
        />
      )}
      {loading ? (
        <Loading />
      ) : (
        <Stack>
          <AssessmentAccordion
            defaultExpanded
            className="w-full"
            title={degreeTitle}
            InfoDetails={
              <Box padding={2} fontSize={14}>
                <AssessmentGrid rows={InfoDetailsRowData} />
              </Box>
            }
            AssessDetails={
              <Box marginY={2}>
                <AcademicAssessmentsContent
                  initialAssessmentValue={
                    initialValue?.convertedGPA === undefined ||
                    initialValue?.convertedGPA === -1
                      ? defaultssessmentValue
                      : initialValue
                  }
                  dialogField={dialogField}
                  onSubmit={handleSubmit}
                  GPAConvertDisplay={GPAConvertDisplay}
                  isViewMode={isViewMode}
                  onJumpToNext={onJumpToNext}
                />
              </Box>
            }
          />
          <hr />
        </Stack>
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  hr {
    width: 100%;
    justify-content: flex-start;
    background-image: linear-gradient(to right, #666, #ccc, #fff);
  }
`;
